import React, { FC, ReactNode, useMemo } from 'react';
import { Form as FinalForm, FormRenderProps } from 'react-final-form';
import { Alert, Button, Flex, Result, Spin, Typography } from 'antd';

import useRequestHandler from '../../../../../hooks/useRequestHandler';

import FormBase from '../../../../../components_/Form';
import FormAntButton from '../FormAntButton';
import { SecTreeToolbarActions } from '../../SecTreePageHeader';
import { getNodeLabel } from '../../treeUtils';
import { LoadedSecurableTypes } from '../../../../../utils/parseSecurableTypes';
import { IProps as IFormProps } from './index';
import List from './List';

import '../PermissionsTable.css';

interface IValues {
  actions: Record<string, Record<string, Record<string, boolean>>>;
}

type IRender = (props: FormRenderProps<IValues>) => ReactNode;

export type LoadedPermissions = LoadedSecurableTypes;

interface IProps {
  data: IFormProps['data'];
  secTreeHierarchy: IFormProps['secTreeHierarchy'];
  treeItem: string | null;
  loadedPermissions: LoadedPermissions | null;
  isPermissionsLoading: boolean;
  isPermissionsError: boolean;
  formResetKey: number;
  onSubmitAction: any;
  onCancel: () => Promise<void>;
}

const defaultValues: IValues = {
  actions: {}
};

const Form: FC<IProps> = ({
  data,
  secTreeHierarchy,
  treeItem,
  onSubmitAction,
  onCancel,
  loadedPermissions,
  isPermissionsLoading,
  isPermissionsError,
  formResetKey
}) => {
  const { roles, nodeACL } = data;

  const requestHandler = useRequestHandler();
  const showInitialSpinner = !loadedPermissions && isPermissionsLoading;
  const showOverlay = Boolean(loadedPermissions) && isPermissionsLoading;

  const displayTreeItem =
    isPermissionsLoading && loadedPermissions ? loadedPermissions.treeItem : treeItem;

  const panelTitle = useMemo(
    () => (displayTreeItem ? getNodeLabel(secTreeHierarchy, displayTreeItem) : ''),
    [secTreeHierarchy, displayTreeItem]
  );

  const initialValues: IValues = useMemo(
    () => ({
      ...defaultValues,
      actions: nodeACL
    }),
    [nodeACL, formResetKey]
  );

  const onSubmit = async (values: IValues) => {
    if (!loadedPermissions?.treeItem) {
      return;
    }

    const error = await onSubmitAction(values);

    requestHandler({
      error,
      entity: 'right',
      onSuccess: onCancel,
      isCreate: true
    });
  };

  const render: IRender = ({ handleSubmit }) => (
    <FormBase style={{ height: '100%' }} onSubmit={handleSubmit}>
      <SecTreeToolbarActions>
        <Button onClick={onCancel}>Отмена</Button>
        <FormAntButton>Сохранить</FormAntButton>
      </SecTreeToolbarActions>
      <div className="sec-tree-permissions-card">
        <div className="sec-tree-permissions-header">
          <Typography.Title level={4} className="sec-tree-permissions-title">
            {panelTitle}
          </Typography.Title>
        </div>
        <fieldset
          className="sec-tree-permissions-fieldset"
          disabled={isPermissionsLoading}
        >
          <List
            key={loadedPermissions?.treeItem}
            treeItem={loadedPermissions?.treeItem ?? ''}
            list={loadedPermissions?.securableTypes ?? []}
            roles={roles}
          />
        </fieldset>
      </div>
    </FormBase>
  );

  if (isPermissionsError && !loadedPermissions) {
    return (
      <div className="sec-tree-permissions-panel">
        <Result status="500" title="Ошибка сервера" />
      </div>
    );
  }

  return (
    <div className="sec-tree-permissions-panel">
      {isPermissionsError && loadedPermissions && (
        <Alert type="error" message="Ошибка сервера" showIcon className="sec-tree-permissions-error" />
      )}
      {showInitialSpinner && (
        <div className="sec-tree-permissions-card">
          <div className="sec-tree-permissions-header">
            <Typography.Title level={4} className="sec-tree-permissions-title">
              {panelTitle}
            </Typography.Title>
          </div>
          <Flex justify="center" align="center" className="sec-tree-permissions-initial-loading">
            <Spin size="large" />
          </Flex>
        </div>
      )}
      {loadedPermissions && (
        <div className="sec-tree-permissions-panel-content">
          <FinalForm
            key={formResetKey}
            initialValues={initialValues}
            onSubmit={onSubmit}
            render={render}
          />
          {showOverlay && (
            <div className="sec-tree-permissions-overlay">
              <Spin size="large" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Form;
